import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useMemo, useState, useEffect, useRef } from 'react'

import { useQuinceRouter } from '@src/modules/navigation'
import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import {
  CheckboxGroup,
  ChipGroup,
  ColorFilterGroup,
  RadioButtonGroup,
  Skeleton,
} from '@src/quince-components/src/atoms'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FilterCollapseGroup } from '../../components/FilterCollapseGroup'
import { FilterDropdown } from '../../components/FilterDropdown'
import { SORT_OPTIONS_CLP_EXPERIMENT, FILTER_DROPDOWN_TYPES, MAX_FILTERS } from '../../constants/constants'
import { FALLBACK_COLOR, FILTER_TYPES, CLEAR_ALL_THRESHOLD } from '../../constants/filterSection.constants'
import { filterListOnExp, useShouldHideDeptAndCategoryFilter } from '../../filterListOnExp'
import { useEnhancedFilterState } from '../../hooks/useEnhancedFilterState'
import { FilterWithSidebarProps, FilterItem, UpdateQueryParams } from '../filterSection.interface'
import {
  createCheckboxOptions,
  excludeDepartmentFilter,
  handleRadioFilterChange,
  prepareRadioFilterData,
  transformSubCollectionCards,
} from '../filterUtils'

import styles from './filterWithSidebar.module.scss'

interface FilterWithSidebarItemCountAndSortProps {
  noOfItems: number
  sortBy?: string
  skipSorting?: boolean
  updateQuery: (params: UpdateQueryParams) => void
  className?: string
}

export const FilterWithSidebarItemCountAndSort: React.FC<FilterWithSidebarItemCountAndSortProps> = ({
  noOfItems,
  sortBy = '',
  skipSorting = false,
  updateQuery,
  className,
}) => {
  const { t } = useTranslation('common')

  // Handle sort change
  const handleSortChange = (value: string) => {
    updateQuery({ sort: { type: 'toggle', value } })
  }

  // Render the sort dropdown component
  const renderSortDropdown = () => {
    if (skipSorting) return null

    const selectedSortOption = SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.SORT}
        label={
          <span>
            {t('sort_by')}
            {`: ${t(selectedSortOption?.labelKey ?? '')}`}
          </span>
        }
        value={sortBy}
        onChange={handleSortChange}
        placement="bottomRight"
        menuClassName={styles.sortDropdownMenu}
        buttonClassName={styles.sortDropdown}
        isMobile={false}
        isActive={sortBy !== ''}
        onTriggerClick={(_, isOpen) => {
          if (isOpen) {
            trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
          }
        }}
      />
    )
  }

  return (
    <div className={classNames(styles.itemCountAndSortContainer, className)}>
      <div className={styles.itemCount}>
        {noOfItems} {t('items')}
      </div>
      <div className={styles.sortContainer}>{renderSortDropdown()}</div>
    </div>
  )
}

export const FilterWithSidebar: React.FC<FilterWithSidebarProps> = props => {
  const {
    className,
    filters: serverFilters = [],
    selectedFilters = {},
    hideFilters = false,
    collectionSlug,
    updateQuery = (_params: UpdateQueryParams) => {
      /* no-op */
    },
    showShimmer = false,
    showGroupedPage = false,
    subCollectionCards,
  } = props

  const { t } = useTranslation('common')
  const router = useQuinceRouter()
  const routerAsPath = router.asPath.split('?')[0] ?? ''

  const [activeFilters, setActiveFilters] = useState<string[]>([])

  const typedSubCollectionCards = useMemo(() => transformSubCollectionCards(subCollectionCards), [subCollectionCards])

  // Use the enhanced filter state hook with subcollection cards
  const {
    selectedFiltersState,
    selectedFilterChips,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
    effectiveTotalSelectedFilters,
  } = useEnhancedFilterState({
    selectedFilters,
    showGroupedPage,
    routerAsPath,
    serverFilters,
    updateQuery,
    subCollectionCards: typedSubCollectionCards,
  })

  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  // Determine if header is visible
  const isHeaderVisible = !isScrollingDown

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()

  const filterSectionRef = useRef<HTMLDivElement>(null)

  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    )
      .filter(item => item.values.length > 1)
      .slice(0, MAX_FILTERS)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  const routerContext = useMemo(() => ({ router, routerAsPath }), [router, routerAsPath])
  const commonParams = useMemo(
    () => ({ showGroupedPage, updateQuery, filters, subCollectionCards: typedSubCollectionCards, serverFilters }),
    [showGroupedPage, updateQuery, filters, typedSubCollectionCards, serverFilters],
  )

  const activeFilterNames = useMemo(() => filters.map(filter => filter.name), [filters])

  useEffect(() => {
    setActiveFilters(activeFilterNames)
  }, [activeFilterNames])

  useEffect(() => {
    if (filterSectionRef.current) {
      filterSectionRef.current.scrollTo({ top: 0, behavior: 'auto' })
    }
  }, [collectionSlug])

  const handleFilterCollapseChange = (activeKeys: string[]) => {
    const newlyOpenedFilters = activeKeys.filter(key => !activeFilters.includes(key))
    newlyOpenedFilters.forEach(filterKey => {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    })

    setActiveFilters(activeKeys)
  }

  // If hideFilters is true or there are no filters, don't render anything
  if (hideFilters || !filters.length) {
    return null
  }

  // Helper function to render a checkbox filter
  const renderCheckboxFilter = (filter: FilterItem) => {
    const state = selectedFiltersState[filter.name] ?? []

    return (
      <CheckboxGroup
        key={`sidebar-${filter.name}-${state.length}`}
        options={createCheckboxOptions(filter.values, state, filter.name)}
        onChange={selectedOptions => {
          handleCheckboxFilterChange(filter, selectedOptions)
        }}
        direction="column"
        maxVisible={8}
        className={styles.checkboxGroup}
        showMoreButtonClassName={styles.showMoreButtonDrawer}
      />
    )
  }

  // Helper function to render a color filter
  const renderColorFilter = (filter: FilterItem) => {
    const colorOptions = filter.values.map(value => {
      if (typeof value === 'string') {
        return {
          value,
          hex: FALLBACK_COLOR,
          label: value,
        }
      }

      const colorValue = value
      return {
        value: colorValue.name,
        hex: colorValue.hex ?? FALLBACK_COLOR,
        label: colorValue.displayName ?? colorValue.name,
        imgURL: colorValue.imgURL ?? undefined,
      }
    })

    return (
      <ColorFilterGroup
        key={`sidebar-color-group-${selectedFiltersState[filter.name]?.length}`}
        options={colorOptions}
        selectedValues={selectedFiltersState[filter.name] ?? []}
        onChange={value => {
          handleColorChange(filter, value)
        }}
        direction="horizontal"
        circleClassName={styles.colorFilterCircle}
        columns={5}
        circleSize={2.2}
        rowGap={1.6}
        columnGap={0}
        className={styles.colorFilterGroupDrawer}
      />
    )
  }

  // Add renderRadioFilter function
  const renderRadioFilter = (filter: FilterItem) => {
    const { filterName, currentValue, options } = prepareRadioFilterData(
      filter,
      selectedFiltersState,
      showGroupedPage,
      routerAsPath,
      typedSubCollectionCards,
    )

    const handleRadioChange = (value: string) => {
      handleRadioFilterChange(routerContext, { ...commonParams, value, options, filter })
    }

    return (
      <RadioButtonGroup
        key={`sidebar-radio-group-${selectedFiltersState[filterName]?.length}`}
        options={options.map(opt => ({
          label: opt.label,
          value: opt.id,
          checked: opt.checked,
        }))}
        onChange={handleRadioChange}
        name={filterName}
        className={styles.radioGroup}
        value={options.find(opt => opt.label === currentValue)?.id}
        itemsClassName={styles.radioItems}
        radioItemClassName={styles.radioItem}
        radioLabelClassName={styles.radioLabel}
      />
    )
  }

  // Unified function to render filter component
  const renderFilterComponent = (filter: FilterItem) => {
    const { config, name } = filter
    const filterType = config.type.toLowerCase()
    const filterName = name.toLowerCase()

    switch (filterType) {
      case FILTER_TYPES.CARD:
        return renderRadioFilter(filter)

      case FILTER_TYPES.CHECKBOX:
        if (filterName === EFilterKeys.COLORS) {
          return renderColorFilter(filter)
        }
        return renderCheckboxFilter(filter)

      default:
        return null
    }
  }

  if (showShimmer) {
    return <Skeleton className={styles.filterWithSidebarSkeleton} />
  }

  return (
    <div
      className={classNames(
        styles.sidebarContainer,
        isHeaderVisible ? styles.sidebarContainerHeaderVisible : styles.sidebarContainerHeaderHidden,
        effectiveTotalSelectedFilters > 0 && 'has-selected-filters',
        className,
      )}
      ref={filterSectionRef}
    >
      <div className={styles.sidebarHeaderContainer}>
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarTitle}>
            {t('filter')} {effectiveTotalSelectedFilters > 0 && `(${effectiveTotalSelectedFilters})`}
          </div>
        </div>

        {selectedFilterChips.length > 0 && (
          <div className={styles.selectedFiltersSection}>
            <ChipGroup
              chips={selectedFilterChips.map(chip => ({
                label: chip.label,
                closeable: true,
                key: `${chip.filterType}-${chip.value}`,
                value: chip.value,
              }))}
              onChipDismiss={handleChipDismissByLabel}
              clearAll={false}
              className={styles.selectedFiltersChips}
            />
            {effectiveTotalSelectedFilters >= CLEAR_ALL_THRESHOLD && (
              <button className={styles.clearAllButton} onClick={clearAllFilters}>
                {t('clear_all_v1')}
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.filtersSection}>
        <FilterCollapseGroup
          filters={filters}
          selectedFiltersState={selectedFiltersState}
          activeItems={activeFilters}
          onChange={handleFilterCollapseChange}
          renderFilterComponent={renderFilterComponent}
          isMobile={false}
          className={styles.sidebarCollapseContainer}
        />
      </div>
    </div>
  )
}
